import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Amazon100 from 'assets/images/offers/amazon100.png';
import Amazon150 from 'assets/images/offers/amazon150.png';
import Amazon50 from 'assets/images/offers/amazon50.png';
import Amazon75 from 'assets/images/offers/amazon75.png';
import Aritzia100 from 'assets/images/offers/aritzia100.png';
import Aritzia50 from 'assets/images/offers/aritzia50.png';
import Aritzia75 from 'assets/images/offers/aritzia75.png';
import Mac50 from 'assets/images/offers/mac50.png';
import Mac75 from 'assets/images/offers/mac75.png';
import Mastercard from 'assets/images/offers/mastercard.png';
import Mastercard100 from 'assets/images/offers/mastercard100.png';
import Mastercard25 from 'assets/images/offers/mastercard25.png';
import Mastercard50 from 'assets/images/offers/mastercard50.png';
import Mastercard60 from 'assets/images/offers/mastercard60.png';
import Mastercard75 from 'assets/images/offers/mastercard75.png';
import Visa from 'assets/images/offers/visa.png';
import Visa100 from 'assets/images/offers/visa100.png';
import Visa25 from 'assets/images/offers/visa25.png';
import Visa50 from 'assets/images/offers/visa50.png';
import Visa60 from 'assets/images/offers/visa60.png';
import Visa75 from 'assets/images/offers/visa75.png';
import Zara100 from 'assets/images/offers/zara100.png';
import Zara50 from 'assets/images/offers/zara50.png';
import Zara75 from 'assets/images/offers/zara75.png';
import { COUNTRY } from 'common/constants';
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { RequestReferralPromotion } from 'connect/saga/handlers/offer/request-referral-promotion';
import { StepKey } from 'pages/funnel/StepKey';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { useHookResult } from 'utils/customHooks/use-hook-result';
import Logger from 'utils/Logger';

export enum OfferType {
  NONE = 'none',
  REFERRAL_PROMOTION = 'referral',
  PROMO_CODE = 'promo-code',
}

export const AMAZON_LEGAL_URL_INFO = Object.freeze({
  text: 'Restrictions apply, see',
  urlText: 'amazon.com/gc-legal',
  url: 'https://www.amazon.com/gc-legal',
});

export const OFFER_IMAGES = Object.freeze({
  amazon150: Amazon150,
  amazon100: Amazon100,
  amazon50: Amazon50,
  amazon75: Amazon75,
  aritzia100: Aritzia100,
  aritzia50: Aritzia50,
  aritzia75: Aritzia75,
  mac50: Mac50,
  mac75: Mac75,
  mastercard: Mastercard,
  mastercard25: Mastercard25,
  mastercard50: Mastercard50,
  mastercard60: Mastercard60,
  mastercard75: Mastercard75,
  mastercard100: Mastercard100,
  visa100: Visa100,
  visa25: Visa25,
  visa50: Visa50,
  visa60: Visa60,
  visa75: Visa75,
  zara100: Zara100,
  zara50: Zara50,
  zara75: Zara75,
});

type DefaultOfferImageValues = '' | '50' | '60' | '75' | '100';

export const DEFAULT_OFFER_IMAGES = Object.freeze({
  [COUNTRY.unitedStates]: Object.freeze({
    default: Visa,
    default50: Visa50,
    default60: Visa60,
    default75: Visa75,
    default100: Visa100,
  }),
  [COUNTRY.canada]: Object.freeze({
    default: Mastercard,
    default50: Mastercard50,
    default60: Mastercard60,
    default75: Mastercard75,
    default100: Mastercard100,
  }),
});

interface OfferServiceParams {
  requestOfferState: ReturnType<typeof RequestOffer.selector>;
  requestReferralPromotionState: ReturnType<typeof RequestReferralPromotion.selector>;
  country: string;
}

class OfferService {
  _requestOfferState: OfferServiceParams['requestOfferState'];
  _requestReferralPromotionState: OfferServiceParams['requestReferralPromotionState'];
  _country: OfferServiceParams['country'];

  constructor({ requestOfferState, requestReferralPromotionState, country }: OfferServiceParams) {
    this._requestOfferState = requestOfferState;
    this._requestReferralPromotionState = requestReferralPromotionState;
    this._country = country;
  }

  get isLoading() {
    return Boolean(
      this._requestOfferState?.loading || this._requestReferralPromotionState?.loading,
    );
  }

  get error() {
    return this._requestOfferState?.error || this._requestReferralPromotionState?.error;
  }

  get hasActiveOffer() {
    return this.offerType !== OfferType.NONE;
  }

  get offerType() {
    const offerTypeMap: Record<OfferType, () => boolean | undefined> = {
      [OfferType.REFERRAL_PROMOTION]: () =>
        this._requestReferralPromotionState?.value,
      [OfferType.PROMO_CODE]: () => this._requestOfferState?.value?.active,
      [OfferType.NONE]: () => true,
    };

    return (Object.keys(offerTypeMap) as OfferType[]).find((key) => {
      const isApplicable = offerTypeMap[key];

      if (typeof isApplicable !== 'function') {
        Logger.error('OfferService.offerType: isApplicable is not a function', {
          isApplicable,
        });

        return false;
      }

      return isApplicable();
    }) ?? OfferType.NONE;
  }

  get giftCardType() {
    if (!this.isReferralPromotion) {
      return '';
    }

    return this._requestReferralPromotionState?.value?.giftCardType || '';
  }

  /**
   * Referral displayNames may be misleading to the end user, so we create a programmatic display name instead
   * E.g. "$150 MasterCard when you refer a friend" -> "$150 MasterCard"
   */
  get formattedReferralOfferName(): string | null {
    if (!this.isReferralPromotion) {
      return null;
    }

    const { referralValue, giftCardType } = this._requestReferralPromotionState.value;
    return `$${referralValue} ${giftCardType}`;
  }

  get offerName(): string {
    const offerNameMap = {
      [OfferType.REFERRAL_PROMOTION]: () =>
        this._requestReferralPromotionState?.value?.displayName,
      [OfferType.PROMO_CODE]: () => this._requestOfferState?.value?.displayName,
      [OfferType.NONE]: () => null,
    };

    const getName = offerNameMap[this.offerType];
    if (typeof getName !== 'function') {
      Logger.error('OfferService.offerName: getName is not a function', {
        getName,
      });

      return 'Unknown';
    }

    return getName() || 'Unknown';
  }

  get offerValue() {
    const offerValueMap = {
      [OfferType.REFERRAL_PROMOTION]: () => {
        const promotion = this._requestReferralPromotionState?.value;
        return promotion?.referralValue || promotion?.referrerValue;
      },
      [OfferType.PROMO_CODE]: () => this._requestOfferState?.value?.cost,
      [OfferType.NONE]: () => 0,
    };

    const getValue = offerValueMap[this.offerType];
    if (typeof getValue !== 'function') {
      Logger.error('OfferService.offerValue: getValue is not a function', {
        getValue,
      });

      return 0;
    }

    return getValue();
  }

  get isPromoCode() {
    return this.offerType === OfferType.PROMO_CODE;
  }

  get isReferralPromotion() {
    return this.offerType === OfferType.REFERRAL_PROMOTION;
  }

  get isThirdPartyOffer() {
    return this.isPromoCode && this.offerValue === 0;
  }

  get offerDisclaimer() {
    if (!this.hasActiveOffer) {
      return null;
    }

    const isAmazon = this.offerName.toLowerCase().includes('amazon');
    if (isAmazon) {
      return {
        text: 'Restrictions apply, see',
        urlText: 'amazon.com/gc-legal',
        url: 'https://www.amazon.com/gc-legal',
      };
    }

    return null;
  }

  get offerImage() {
    if (!this.hasActiveOffer) {
      return null;
    }

    if (this.isThirdPartyOffer) {
      return this._requestOfferState.value.images.square;
    }

    const getImageKey = {
      [OfferType.REFERRAL_PROMOTION]: () => {
        const {
          giftCardType,
          referralValue,
        } = this._requestReferralPromotionState.value;
        return `${giftCardType.toLowerCase()}${referralValue}`;
      },
      [OfferType.PROMO_CODE]: () => this._requestOfferState.value.slug,
      [OfferType.NONE]: () => null,
    }[this.offerType];

    if (typeof getImageKey !== 'function') {
      Logger.error('OfferService.offerImage: getImageKey is not a function', {
        getImageKey,
      });

      return null;
    }

    const imageKey = getImageKey() as keyof typeof OFFER_IMAGES;
    if (OFFER_IMAGES[imageKey]) {
      return OFFER_IMAGES[imageKey];
    }

    const country = Object.keys(DEFAULT_OFFER_IMAGES).includes(this._country)
      ? this._country
      : COUNTRY.unitedStates;

    const value = this.offerValue;

    const defaultImagesForCountry = DEFAULT_OFFER_IMAGES[country];
    const defaultImageKey = `default${value as DefaultOfferImageValues}` as keyof typeof defaultImagesForCountry;
    const defaultImage = defaultImagesForCountry?.[defaultImageKey];

    if (defaultImage) {
      return defaultImage;
    }

    return defaultImagesForCountry?.default;
  }
}

export const useOfferService = (): InstanceType<typeof OfferService> => {
  const funnelStorage = useFunnelStorage();

  const requestReferralPromotionState = useSelector(RequestReferralPromotion.selector);
  const requestOfferState = useSelector(RequestOffer.selector);
  const country = funnelStorage?.get(StepKey.LOCATION)?.address?.country;

  const createServiceParams = useCallback(() => ({
    requestOfferState,
    requestReferralPromotionState,
    country,
  }), [requestOfferState, requestReferralPromotionState, country]);

  const createOfferService = useCallback(() => new OfferService(createServiceParams()), [createServiceParams]);

  return useHookResult(createOfferService());
};
